/* ==========================================================================
   AFTERMILLENNIUM — global.js
   Drawers, accordions, quantity, variant picker, delivery zone.
   Vanilla, no dependencies. Progressive enhancement throughout.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     FOCUS TRAP — for drawers and modals
     ------------------------------------------------------------------ */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container) => {
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  /* ------------------------------------------------------------------
     OVERLAY + DRAWER
     ------------------------------------------------------------------ */
  let lastFocused = null;
  let releaseTrap = null;

  const setScrollLock = (locked) => {
    document.body.classList.toggle('is-locked', locked);
  };

  window.AmDrawer = {
    open(id) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      const overlay = drawer.querySelector('.overlay') || document.querySelector('.overlay');
      const previous = document.querySelector('.drawer.is-open');
      if (previous) this.close(previous.id);
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-open');
      setScrollLock(true);
      releaseTrap = trapFocus(drawer);
      const first = drawer.querySelector(FOCUSABLE);
      if (first) setTimeout(() => first.focus(), 60);
    },

    close(id) {
      const drawer = id ? document.getElementById(id) : document.querySelector('.drawer.is-open');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      const overlay = document.querySelector('.overlay');
      if (overlay) overlay.classList.remove('is-open');
      setScrollLock(false);
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      if (lastFocused) lastFocused.focus();
    },

    toggle(id) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      drawer.classList.contains('is-open') ? this.close(id) : this.open(id);
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      AmDrawer.open(opener.getAttribute('data-drawer-open'));
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      if (!closer.matches('a[href]')) e.preventDefault();
      AmDrawer.close(closer.getAttribute('data-drawer-close'));
      return;
    }
    if (e.target.classList.contains('overlay')) AmDrawer.close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const open = document.querySelector('.drawer.is-open');
      if (open) AmDrawer.close(open.id);
      document.querySelectorAll('.mega.is-open').forEach((m) => {
        m.classList.remove('is-open');
        const t = m.closest('.hdr__item');
        const btn = t && t.querySelector('.hdr__link');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ------------------------------------------------------------------
     ACCORDION — data-accordion on the container
     ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const head = e.target.closest('[data-acc-head]');
    if (!head) return;
    e.preventDefault();
    const panel = head.nextElementSibling;
    if (!panel) return;
    const open = head.getAttribute('aria-expanded') === 'true';
    head.setAttribute('aria-expanded', String(!open));
    panel.inert = open;
    if (panel.classList.contains('acc__panel')) {
      head.closest('.acc')?.querySelectorAll('[data-acc-head]').forEach((other) => {
        if (other !== head) other.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ------------------------------------------------------------------
     QUANTITY
     ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    e.preventDefault();
    const wrap = btn.closest('.qty');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const step = Number(btn.getAttribute('data-qty-step'));
    const min = Number(input.getAttribute('min') || 1);
    const max = Number(input.getAttribute('max') || 99);
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + step));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     PDP — variant selection
     Separate colour / size selectors (not a combined dropdown) because
     combined pickers are a measurable driver of sizing errors and returns.
     ------------------------------------------------------------------ */
  const initProduct = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const variants = JSON.parse(root.getAttribute('data-product-json') || '[]');
    const form = root.querySelector('.pdp__form');
    if (!form || !variants.length) return;
    const idInput = form.querySelector('[name="id"]');
    const lists = [...root.querySelectorAll('[data-option-list]')];
    const initial = variants.find(v => String(v.id) === idInput.value) || variants[0];
    let selected = [...initial.options];
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'it', {
      style: 'currency', currency: root.dataset.currency || 'EUR'
    }).format(value / 100);
    const showMedia = id => {
      root.querySelectorAll('[data-product-media]').forEach(media => {
        media.hidden = media.dataset.productMedia !== String(id);
        if (media.hidden) media.querySelectorAll('video').forEach(video => video.pause());
      });
      root.querySelectorAll('[data-thumb]').forEach(thumb => {
        const active = thumb.dataset.mediaId === String(id);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };
    root.querySelectorAll('[data-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaId));
    });
    const render = (updateUrl = false) => {
      const variant = variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      lists.forEach((list, i) => {
        list.querySelectorAll('[data-option-value]').forEach(button => {
          const value = button.dataset.optionValue;
          const active = value === selected[i];
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
          // Earlier options determine which later options are available.
          button.disabled = !variants.some(v => v.available && v.options[i] === value &&
            v.options.slice(0, i).every((opt, index) => opt === selected[index]));
        });
      });
      root.querySelectorAll('[data-option-value-current]').forEach(el => {
        el.textContent = selected[Number(el.dataset.optionValueCurrent)] || '';
      });
      idInput.value = variant ? variant.id : '';
      form.querySelectorAll('[name="add"], [name="buy"]').forEach(button => {
        button.disabled = !variant?.available;
      });
      const soldOut = root.querySelector('[data-soldout]');
      if (soldOut) soldOut.hidden = !!variant?.available;
      if (!variant) return;
      const price = root.querySelector('[data-price-current]');
      const compare = root.querySelector('[data-price-compare]');
      if (price) {
        price.textContent = money(variant.price);
        price.classList.toggle('pdp__price--sale', variant.compare_at_price > variant.price);
      }
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = compare.hidden ? '' : money(variant.compare_at_price);
      }
      const meta = root.querySelector('[data-variant-meta]');
      if (meta) meta.textContent = variant.sku || '';
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      if (variant.featured_media) showMedia(variant.featured_media.id);
      document.dispatchEvent(new CustomEvent('am:variant-change', {
        detail: { id: variant.id, price: money(variant.price),
                  available: variant.available, compareAtPrice: variant.compare_at_price || 0 },
      }));
    };
    lists.forEach((list, i) => {
      list.addEventListener('click', event => {
        const button = event.target.closest('[data-option-value]');
        if (!button || button.disabled) return;
        selected[i] = button.dataset.optionValue;
        const match = variants.find(v => v.available && v.options.every((opt, index) => opt === selected[index]));
        if (!match) {
          const fallback = variants.find(v => v.available && v.options.slice(0, i + 1).every((opt, index) => opt === selected[index]));
          if (fallback) selected = [...fallback.options];
        }
        render(true);
      });
    });
    form.addEventListener('submit', event => {
      const redirect = form.querySelector('[name="return_to"]');
      if (redirect) redirect.value = event.submitter?.matches('[data-buy-now]') ? '/checkout' : (window.Shopify?.routes?.root || '/') + 'cart';
    });
    render();
  };

  const init = () => {
    document.querySelectorAll('[data-product-root]').forEach(initProduct);
    document.querySelectorAll('[data-acc-head]').forEach(head => {
      if (head.nextElementSibling) head.nextElementSibling.inert = head.getAttribute('aria-expanded') !== 'true';
    });
    document.querySelectorAll('.drawer:not(.is-open)').forEach(drawer => drawer.setAttribute('inert', ''));
    document.querySelectorAll('[data-mega-item]').forEach(item => {
      if (item.dataset.initialized) return;
      item.dataset.initialized = 'true';
      const button = item.querySelector('.hdr__link');
      const panel = item.querySelector('[data-mega]');
      const close = () => {
        panel.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      };
      button.addEventListener('click', () => {
        const opening = button.getAttribute('aria-expanded') !== 'true';
        document.querySelectorAll('[data-mega-item] .hdr__link[aria-expanded="true"]').forEach(other => {
          other.setAttribute('aria-expanded', 'false');
          other.nextElementSibling.classList.remove('is-open');
        });
        panel.classList.toggle('is-open', opening);
        button.setAttribute('aria-expanded', String(opening));
      });
      // Once a panel is open, sliding across the bar moves it — deliberate
      // click to open, hover to change panels, no flicker.
      button.addEventListener('mouseenter', () => {
        const openBtn = document.querySelector('[data-mega-item] .hdr__link[aria-expanded="true"]');
        if (openBtn && openBtn !== button) button.click();
      });
      item.addEventListener('keydown', event => {
        if (event.key === 'Escape') { close(); button.focus(); }
      });
      item.addEventListener('focusout', event => { if (!item.contains(event.relatedTarget)) close(); });
      document.addEventListener('click', event => { if (!item.contains(event.target)) close(); });
    });
  };
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  /* ---------- CART QUANTITY / REMOVE ----------
     Drawer lines post to /cart/change.js per line (native /cart/change
     POST is an AJAX-only endpoint — a full submit lands on an error page).
     Cart-page lines submit their updates[] form natively. */
  window.amCartChange = async function (key, qty) {
    try {
      const body = new URLSearchParams({ id: key, quantity: String(qty) });
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body,
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('change failed');
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count || '';
        el.hidden = !(cart.item_count > 0);
        const trigger = el.closest('[aria-label]');
        if (trigger) trigger.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/\(\d+\)$/, '(' + cart.item_count + ')'));
      });
      const sec = await fetch((window.Shopify?.routes?.root || '/') + '?sections=cart-drawer').then(r => r.json());
      const html = sec['cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('CartDrawer');
        const cur = document.getElementById('CartDrawer');
        if (fresh && cur) cur.innerHTML = fresh.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('am:cart-updated', { detail: { count: cart.item_count } }));
    } catch (e) {
      location.reload();
    }
  };

  let lastChangedKey = null;
  let lastChangedQty = null;
  document.addEventListener('change', event => {
    const input = event.target.closest('input[name="updates[]"]');
    if (!input) return;
    if (input.hasAttribute('data-key') && document.getElementById('CartDrawer') && document.getElementById('CartDrawer').contains(input)) {
      lastChangedKey = input.getAttribute('data-key');
      lastChangedQty = parseInt(input.value, 10) || 0;
      window.amCartChange(lastChangedKey, lastChangedQty);
      return;
    }
    if (input.form) input.form.requestSubmit();
  });

  /* The drawer form's action is /cart/change — an AJAX-only endpoint. Any
     native submit of it (Enter key, requestSubmit) must be converted to
     the AJAX call. */
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form.action || form.action.indexOf('/cart/change') === -1) return;
    e.preventDefault();
    if (lastChangedKey) window.amCartChange(lastChangedKey, lastChangedQty);
  });

  /* remove buttons: drawer (data-key) and cart page share the handler */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    window.amCartChange(btn.getAttribute('data-key'), 0);
  });

  /* ------------------------------------------------------------------
     STICKY BUY BAR — mirrors the PDP form; visible when it scrolls away.
     ------------------------------------------------------------------ */
  const initStickyBar = () => {
    const bar = document.querySelector('[data-sticky-bar]');
    const form = document.querySelector('.pdp__form');
    if (!bar || !form || bar.dataset.initialized) return;
    bar.dataset.initialized = 'true';

    const quantity = form.querySelector('[name="quantity"]');
    const stickyQuantity = bar.querySelector('[name="quantity"]');
    quantity?.addEventListener('change', () => {
      if (stickyQuantity) stickyQuantity.value = quantity.value;
    });
    const sentinel = form.querySelector('[data-buy-now]') || form;
    new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.inert = !show;
      },
      { rootMargin: '0px 0px -72px 0px' }
    ).observe(sentinel);

    // Mirror the PDP variant loop: id, price, availability.
    document.addEventListener('am:variant-change', (e) => {
        const { id, price, available, compareAtPrice } = e.detail;
        bar.querySelector('[data-sticky-variant-id]').value = id;
        const priceEl = bar.querySelector('[data-sticky-price]');
        priceEl.textContent = price;
        priceEl.classList.toggle('pdp__price--sale', compareAtPrice > 0);
        bar.querySelector('button[type=submit]').disabled = !available;
      });
  };
  document.addEventListener('DOMContentLoaded', initStickyBar);
  document.addEventListener('shopify:section:load', initStickyBar);

  /* ------------------------------------------------------------------
     FETCH QUICK-ADD — sticky bar + cart-drawer upsell add without a
     page reload; refreshes the drawer in place.
     ------------------------------------------------------------------ */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button[type=submit]');
    if (btn) btn.disabled = true;
    let added = false;
    try {
      const body = new URLSearchParams(new FormData(form));
      body.set('sections', 'cart-drawer');
      const res = await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'add failed');
      added = true;
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const itemCount = cart.item_count;
      // Refresh the drawer from the Section Rendering API (the sections
      // param renders it server-side with the new cart state).
      const sec = await fetch(`?sections=cart-drawer`).then((r) => r.json());
      const html = sec['cart-drawer'];
      if (html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('#CartDrawer');
        const current = document.getElementById('CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = itemCount || '';
          el.hidden = !(itemCount > 0);
          const trigger = el.closest('[aria-label]');
          if (trigger) trigger.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/\(\d+\)$/, '(' + itemCount + ')'));
        });
        document.querySelectorAll('#CartDrawer [data-cart-add]').forEach((f) => { f.dataset.initialized = ''; });
      }
      document.dispatchEvent(new CustomEvent('am:cart-updated', { detail: { count: itemCount } }));
      if (!document.getElementById('CartDrawer').classList.contains('is-open')) {
        window.AmDrawer && window.AmDrawer.open('CartDrawer');
      }
    } catch (err) {
      console.warn('cart add failed', err);
      if (added) location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      else form.submit();
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  /* ------------------------------------------------------------------
      STICKY HEADER — compact the bar once scrolled
      ------------------------------------------------------------------
      Toggles .is-stuck rather than writing an inline colour, so the header
      owns its own styling and the compact height can be a CSS transition
      instead of a jump. rAF-throttled because this fires on every scroll
      event, and guarded so it re-binds safely after a theme-editor section
      reload rather than stacking listeners. */
  const initSticky = () => {
    const hdr = document.querySelector('[data-sticky-header]');
    if (!hdr || hdr.dataset.stickyBound) return;
    hdr.dataset.stickyBound = '';
    let queued = false;
    const apply = () => {
      queued = false;
      hdr.classList.toggle('is-stuck', window.scrollY > 8);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(apply);
      },
      { passive: true }
    );
    apply();
  };
  document.addEventListener('DOMContentLoaded', initSticky);
  document.addEventListener('shopify:section:load', initSticky);
})();

